import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Settings } from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
} from "@tabipla/ui";
import { useUpdateGroup } from "../../../application/hooks/useGroups";

interface GroupSettingsDialogProps {
  groupId: string;
  groupName: string;
  groupDescription: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GroupSettingsDialog({
  groupId,
  groupName,
  groupDescription,
  open,
  onOpenChange,
}: GroupSettingsDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(groupName);
  const [description, setDescription] = useState(groupDescription ?? "");

  const updateGroupMutation = useUpdateGroup(groupId);

  // Sync with props when dialog opens
  useEffect(() => {
    if (open) {
      setName(groupName);
      setDescription(groupDescription ?? "");
    }
  }, [open, groupName, groupDescription]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateGroupMutation.mutateAsync({
        name,
        description: description || null,
      });
      toast.success(t("groups.updateSuccess", "わりかんを更新しました"));
      onOpenChange(false);
    } catch {
      toast.error(t("groups.updateError", "更新に失敗しました"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-emerald-600" />
            {t("groups.settingsTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("groups.settingsDescription", "わりかんの設定を変更します")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("groups.name")}</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("groups.namePlaceholder")}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("groups.description")}
                <span className="ml-1 text-xs text-muted-foreground">({t("common.optional")})</span>
              </label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("groups.descriptionPlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateGroupMutation.isPending || !name.trim()}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 sm:flex-none"
            >
              {updateGroupMutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
